import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Landmark } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const STATUSES = ['submitted', 'contacted', 'approved', 'funded', 'rejected'];

export default function MortgageReferrals() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['mortgage-referrals'],
    queryFn: async () => {
      const { data } = await supabase.from('mortgage_referrals' as any)
        .select('*').order('created_at', { ascending: false });
      return (data as any[]) || [];
    },
  });

  const update = async (id: string, patch: any) => {
    const { error } = await supabase.from('mortgage_referrals' as any).update(patch).eq('id', id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: ['mortgage-referrals'] });
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <h1 className="text-2xl font-bold flex items-center gap-2">
        <Landmark className="h-6 w-6 text-primary" /> Mortgage Referrals
      </h1>

      <Card>
        <CardHeader><CardTitle className="text-base">All referrals</CardTitle></CardHeader>
        <CardContent>
          {!data?.length ? <p className="text-sm text-muted-foreground py-6 text-center">No referrals yet.</p> : (
            <div className="divide-y">
              {data.map((r) => (
                <div key={r.id} className="py-3 grid md:grid-cols-5 gap-3 items-center text-sm">
                  <div>
                    <p className="font-medium">{r.full_name || r.email || 'Anon'}</p>
                    <p className="text-xs text-muted-foreground">{r.partner_bank} · {format(new Date(r.created_at), 'd MMM yyyy')}</p>
                  </div>
                  <div className="text-xs">
                    <p>Loan: <strong>{Number(r.loan_amount_etb || 0).toLocaleString()} ETB</strong></p>
                    <p>Term: {r.loan_term_years || '—'}y</p>
                  </div>
                  <select
                    value={r.status}
                    onChange={(e) => update(r.id, { status: e.target.value, paid_at: e.target.value === 'funded' ? new Date().toISOString() : r.paid_at })}
                    className="border rounded px-2 py-1"
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <Input type="number" defaultValue={r.payout_etb || ''} placeholder="Payout ETB"
                    onBlur={(e) => update(r.id, { payout_etb: Number(e.target.value) || null })} />
                  <Badge variant={r.status === 'funded' ? 'default' : r.status === 'rejected' ? 'destructive' : 'secondary'} className="capitalize justify-self-end">
                    {r.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
